
import {jwtDecode} from 'jwt-decode';

export function tokenIsExpired(token) {
  try {
    const decodedToken = jwtDecode(token);
    const expirationTime = decodedToken.exp * 1000;

    if (!decodedToken || expirationTime < Date.now()) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
}

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token
 * @returns {number} - Expiration time in milliseconds, or 0 if invalid
 */
export function getTokenExpirationTime(token) {
  try {
    if (!token) return 0;
    
    const decodedToken = jwtDecode(token);
    return decodedToken.exp * 1000;
  } catch (error) {
    console.error('Error getting token expiration time:', error);
    return 0;
  }
}

/**
 * Get remaining time until token expires
 * @param {string} token - JWT token
 * @returns {number} - Remaining time in milliseconds, or 0 if expired/invalid
 */
export function getTokenTimeRemaining(token) {
  try {
    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) return 0;
    
    const currentTime = Date.now();
    const remaining = expirationTime - currentTime;
    
    return Math.max(0, remaining);
  } catch (error) {
    console.error('Error calculating token time remaining:', error);
    return 0;
  }
}

/**
 * Check if token will expire within specified minutes
 * @param {string} token - JWT token
 * @param {number} minutes - Minutes to check ahead
 * @returns {boolean} - True if token expires within specified minutes
 */
export function tokenExpiresWithin(token, minutes) {
  try {
    const remaining = getTokenTimeRemaining(token);
    const thresholdMs = minutes * 60 * 1000;
    
    return remaining <= thresholdMs;
  } catch (error) {
    console.error('Error checking token expiration threshold:', error);
    return true;
  }
}

/**
 * Validate token structure and basic properties
 * @param {string} token - JWT token
 * @returns {boolean} - True if token structure is valid
 */
export function isValidTokenStructure(token) {
  try {
    if (!token || typeof token !== 'string') {
      return false;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    const decodedToken = jwtDecode(token);
    
    // Check for required fields
    if (!decodedToken.exp || !decodedToken.userId || !decodedToken.companyId) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating token structure:', error);
    return false;
  }
}

/**
 * Get user information from token
 * @param {string} token - JWT token
 * @returns {object|null} - User information or null if invalid
 */
export function getUserFromToken(token) {
  try {
    if (!isValidTokenStructure(token)) {
      return null;
    }

    const decodedToken = jwtDecode(token);
    
    return {
      userId: decodedToken.userId,
      username: decodedToken.username,
      companyId: decodedToken.companyId,
      branchId: decodedToken.branchId,
      roleName: decodedToken.roleName,
      isSuperAdmin: decodedToken.isSuperAdmin?.toLowerCase() === 'true',
      exp: decodedToken.exp,
      iat: decodedToken.iat
    };
  } catch (error) {
    console.error('Error extracting user from token:', error);
    return null;
  }
}
